/**
 * Session ID management for anonymous user tracking
 *
 * Generates and persists a unique session ID in localStorage for tracking
 * anonymous visitor behavior across page reloads.
 */

const SESSION_ID_KEY = 'wallstreetle_session_id';

/**
 * Gets the existing session ID from localStorage, or creates a new one if none exists.
 *
 * The session ID persists across page reloads and browser sessions, allowing
 * tracking of user behavior even for anonymous visitors.
 *
 * @returns {string} A UUID v4 session identifier
 */
export function getOrCreateSessionId(): string {
  try {
    // Try to get existing session ID
    let sessionId = localStorage.getItem(SESSION_ID_KEY);

    if (!sessionId) {
      // Generate new session ID using crypto.randomUUID
      sessionId = crypto.randomUUID();
      localStorage.setItem(SESSION_ID_KEY, sessionId);
    }

    return sessionId;
  } catch (error) {
    // Fallback if localStorage is unavailable (e.g., private browsing)
    // Generate a temporary session ID that won't persist
    console.warn('localStorage unavailable, using temporary session ID:', error);
    return crypto.randomUUID();
  }
}

/**
 * Clears the session ID from localStorage.
 * Useful for testing or if user wants to reset their session.
 */
export function clearSessionId(): void {
  try {
    localStorage.removeItem(SESSION_ID_KEY);
  } catch (error) {
    console.warn('Failed to clear session ID:', error);
  }
}

/**
 * Gets the current session ID without creating a new one.
 * Returns null if no session ID exists.
 *
 * @returns {string | null} The current session ID or null
 */
export function getSessionId(): string | null {
  try {
    return localStorage.getItem(SESSION_ID_KEY);
  } catch (error) {
    console.warn('Failed to get session ID:', error);
    return null;
  }
}
